import { Button, Col, Modal, Row } from "antd";
import React, { useEffect } from "react";
import {
    CA_DD_MODULES,
    RevstackDashboardModules,
    DASHBOARD_TYPES,
    RevstackDashboardSubModules,
    IRevstackRelativeFilters,
} from "@/ts/common-types";
import { BsBoxes } from "react-icons/bs";
import {
    RevstackTablePerformanceWidget,
    WidgetCard,
} from "@/components/widgets";
import {
    useGetDashboardConfig,
    useRevstackStreamData,
} from "@/hooks/AllCustomHooks";
import {
    resetRevstackChangeSkuWidgetData,
    updateRevstackChangeSkuWidgetData,
    updateRelativeFilters,
    updateMainFilters,
} from "@/features/revstackWidgetsSlice";
import { useDispatch, useSelector } from "react-redux";
import { REVSTACK_THEME_COLORS } from "@/ts/constants";
import { applyCurrentFiltersWithHash } from "@/ts/revstack/filters";

const RevstackChangeSkuModal = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const dispatch = useDispatch();

    const selectedChangeSku = useSelector((state: any) => state.showRevstackPopup?.value?.changeSku?.selectedChangeSku);
    const relativeFilters = useSelector((state: any) => state.relativeFilters.value);
    const relativeFiltersSku = relativeFilters.skus?.[0];
    const mainFilters = useSelector((state: any) => state.mainFilters.value);
    const mainFiltersData = useSelector((state: any) => state.mainFiltersData.value);

    const changeSkuConfig = useGetDashboardConfig(
        RevstackDashboardModules.PRODUCT_ANALYSIS,
        DASHBOARD_TYPES.BASE,
        RevstackDashboardSubModules.PRODUCT_CHANGE_LIST
    );

    const revstackWidgetData = useSelector(
        (state: {
            revstackWidgetData: {
                changeSku: any;
            };
        }) => state.revstackWidgetData
    );

    const { getRevstackWidgetStreamData } = useRevstackStreamData({
        config: changeSkuConfig.widgetConfig,
        streamConfig: {
            resetWidgetDataAction: resetRevstackChangeSkuWidgetData,
            updateWidgetDataAction: updateRevstackChangeSkuWidgetData,
        },
        overviewTabKey: CA_DD_MODULES.OVERVIEW,
    });

    // Only trigger widget request after filters are fetched in RevstackFiltersLayout
    useEffect(() => {
        // For Product Analysis with SKU mapping, these might be empty arrays but selected should have values
        const hasSelectedFilters =
            mainFilters &&
            (mainFilters.categories?.length > 0 ||
                mainFilters.subcategories?.length > 0 ||
                mainFilters.brands?.length > 0);

        if (hasSelectedFilters) {
            getRevstackWidgetStreamData();
        }
    }, [mainFiltersData, mainFilters]);

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            className="revstack-dashboard-scope"
            title={
                <div
                    id="revstack-drilldown-header"
                    className="w-100 d-flex align-items-center"
                    style={{
                        padding: "15px 60px 15px 20px",
                        borderBottom: "1px solid #CBD9E7",
                    }}
                >
                    <i className="icon-btn">{<BsBoxes size={16} />}</i>
                    <h4 className="mb-0 ms-2">SKU List</h4>
                </div>
            }
            width={1100}
            height={700}
            wrapClassName="revstack-change-sku-modal"
            footer={null}
            style={{ top: 20 }}
            styles={{
                header: { padding: "0px", borderRadius: "16px 16px 0 0" },
                body: {
                    padding: 0,
                    backgroundColor: "#F0F4F8",
                    overflowY: "scroll",
                    borderRadius: "0 0 8px 8px",
                },
            }}
        >
            <Row className="w-100 py-3">
                <Col span={24} className="px-4">
                    <WidgetCard bodyPaddingClass="p-0">
                        <RevstackTablePerformanceWidget
                            widgetConfig={changeSkuConfig.widgetConfig[0]}
                            widgetData={revstackWidgetData.changeSku}
                            title={""}
                            configSessionKey={"revstack-change-sku-modal"}
                            modalConfig={{
                                onModalOpen: () => { },
                            }}
                        />
                    </WidgetCard>
                    <div className="d-flex align-content-end justify-content-end">
                        <Button 
                            type="primary"
                            className="mt-3"
                            style={{ backgroundColor: `${REVSTACK_THEME_COLORS.primary[700]}`, padding: "18px 24px", fontSize: "14px" }}
                            disabled={selectedChangeSku === relativeFiltersSku}
                            onClick={async () => {
                                if (!selectedChangeSku) return;

                                
                                // Update relativeFilters in Redux
                                dispatch(updateRelativeFilters({
                                    skus: [selectedChangeSku],
                                } as IRevstackRelativeFilters));

                                // Close modal
                                onClose();

                                // Force component re-render to trigger SKU mapping fetch
                                setTimeout(() => {
                                    dispatch(updateMainFilters({
                                        ...mainFilters,
                                        rerender: !mainFilters.rerender
                                    }));
                                }, 100);

                                await applyCurrentFiltersWithHash({
                                    skus: [selectedChangeSku],
                                });
                            }}
                        >
                            Proceed
                        </Button>
                    </div>

                </Col>
            </Row>
        </Modal>
    );
};

export default RevstackChangeSkuModal;
